import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    RefreshCw, Image as ImageIcon, Play, Sparkles, ExternalLink,
    AlertTriangle, Eye, DollarSign, Target
} from 'lucide-react';
import { formatNumber, formatCurrency } from '../utils/helpers';
import { fetchCampaignCreatives } from '../services/campaignCreativesService';

// Score utilities
const getScoreColor = (score) => {
    if (score >= 7) return 'var(--color-success)';
    if (score >= 5) return 'var(--color-warning)';
    return 'var(--color-error)';
};

const getGrade = (score) => {
    if (score >= 9) return 'A+';
    if (score >= 8) return 'A';
    if (score >= 7) return 'B+';
    if (score >= 6) return 'B';
    if (score >= 5) return 'C';
    return 'D';
};

const CreativesQuickView = ({
    campaignId,
    platform,
    userId,
    brandId,
    dateRange,
    creativeAnalysisCache = {}
}) => {
    const navigate = useNavigate();
    const [creatives, setCreatives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchCreatives();
    }, [campaignId, platform, dateRange]);

    const fetchCreatives = async () => {
        if (!campaignId || !userId || !brandId) return;

        setLoading(true);
        setError(null);

        try {
            const result = await fetchCampaignCreatives(
                campaignId,
                userId,
                brandId,
                platform,
                dateRange
            );
            setCreatives(result.creatives || []);
        } catch (err) {
            console.error('Error fetching creatives:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleViewFullAnalysis = () => {
        navigate('/campaign-creatives');
    };

    // Loading state
    if (loading) {
        return (
            <div style={{ padding: 'var(--space-6)', textAlign: 'center' }}>
                <RefreshCw size={24} className="spin text-secondary" />
                <p className="text-secondary" style={{ marginTop: 'var(--space-2)' }}>
                    Loading creatives...
                </p>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div style={{ padding: 'var(--space-6)', textAlign: 'center' }}>
                <AlertTriangle size={32} className="text-error" style={{ opacity: 0.5 }} />
                <p className="text-error" style={{ marginTop: 'var(--space-2)' }}>
                    {error}
                </p>
                <button
                    onClick={fetchCreatives}
                    className="btn btn-secondary"
                    style={{ marginTop: 'var(--space-3)' }}
                >
                    <RefreshCw size={16} /> Retry
                </button>
            </div>
        );
    }

    // Empty state
    if (creatives.length === 0) {
        return (
            <div style={{ padding: 'var(--space-6)', textAlign: 'center' }}>
                <ImageIcon size={32} className="text-secondary" style={{ opacity: 0.3 }} />
                <p className="text-secondary" style={{ marginTop: 'var(--space-2)' }}>
                    No creatives found for this campaign.
                </p>
            </div>
        );
    }

    // Show first 6 creatives
    const displayCreatives = creatives.slice(0, 6);
    const hasMore = creatives.length > 6;

    return (
        <div style={{ padding: 'var(--space-4)' }}>
            {/* Summary Stats */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 'var(--space-3)',
                marginBottom: 'var(--space-4)'
            }}>
                <div style={{
                    padding: 'var(--space-3)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center'
                }}>
                    <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Creatives</div>
                    <div style={{ fontWeight: 700 }}>{creatives.length}</div>
                </div>
                <div style={{
                    padding: 'var(--space-3)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center'
                }}>
                    <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Active</div>
                    <div style={{ fontWeight: 700, color: 'var(--color-success)' }}>
                        {creatives.filter(c => c.status === 'ACTIVE').length}
                    </div>
                </div>
                <div style={{
                    padding: 'var(--space-3)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center'
                }}>
                    <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Total Spend</div>
                    <div style={{ fontWeight: 700 }}>
                        {formatCurrency(creatives.reduce((sum, c) => sum + (c.metrics?.spend || 0), 0))}
                    </div>
                </div>
                <div style={{
                    padding: 'var(--space-3)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center'
                }}>
                    <div className="text-secondary" style={{ fontSize: 'var(--font-size-xs)' }}>Avg ROAS</div>
                    <div style={{ fontWeight: 700 }}>
                        {(() => {
                            const totalSpend = creatives.reduce((sum, c) => sum + (c.metrics?.spend || 0), 0);
                            const totalRevenue = creatives.reduce((sum, c) => sum + (c.metrics?.revenue || 0), 0);
                            const roas = totalSpend > 0 ? totalRevenue / totalSpend : 0;
                            return (
                                <span style={{
                                    color: roas >= 2 ? 'var(--color-success)' : roas >= 1 ? 'var(--color-warning)' : 'var(--color-error)'
                                }}>
                                    {roas.toFixed(2)}x
                                </span>
                            );
                        })()}
                    </div>
                </div>
            </div>

            {/* Creatives Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 'var(--space-3)',
                marginBottom: 'var(--space-4)'
            }}>
                {displayCreatives.map(creative => {
                    const analysis = creativeAnalysisCache[creative.id];
                    const roas = creative.metrics?.roas || 0;

                    return (
                        <div
                            key={creative.id}
                            style={{
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)',
                                overflow: 'hidden',
                                border: '1px solid var(--color-border-light)'
                            }}
                        >
                            {/* Thumbnail */}
                            <div style={{
                                position: 'relative',
                                paddingTop: '75%',
                                background: 'var(--color-bg-tertiary)'
                            }}>
                                {creative.thumbnailUrl || creative.imageUrl ? (
                                    <img
                                        src={creative.thumbnailUrl || creative.imageUrl}
                                        alt={creative.name}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                    />
                                ) : (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--color-text-tertiary)'
                                    }}>
                                        {creative.isVideo ? <Play size={24} /> : <ImageIcon size={24} />}
                                    </div>
                                )}

                                {/* Video badge */}
                                {creative.isVideo && (
                                    <div style={{
                                        position: 'absolute',
                                        bottom: 4,
                                        left: 4,
                                        padding: '2px 6px',
                                        borderRadius: 'var(--radius-sm)',
                                        background: 'rgba(0,0,0,0.7)',
                                        color: 'white',
                                        fontSize: 'var(--font-size-xs)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4
                                    }}>
                                        <Play size={10} /> Video
                                    </div>
                                )}

                                {/* AI Score badge */}
                                {analysis && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        padding: '2px 6px',
                                        borderRadius: 'var(--radius-sm)',
                                        background: getScoreColor(analysis.overallScore),
                                        color: 'white',
                                        fontSize: 'var(--font-size-xs)',
                                        fontWeight: 600
                                    }}>
                                        {getGrade(analysis.overallScore)}
                                    </div>
                                )}
                            </div>

                            {/* Info */}
                            <div style={{ padding: 'var(--space-2)' }}>
                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    marginBottom: 'var(--space-1)'
                                }}>
                                    {creative.name}
                                </div>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    <span>{formatCurrency(creative.metrics?.spend || 0)}</span>
                                    <span style={{
                                        color: roas >= 2 ? 'var(--color-success)' : roas >= 1 ? 'var(--color-warning)' : 'var(--color-error)',
                                        fontWeight: 600
                                    }}>
                                        {roas.toFixed(1)}x
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* View Full Analysis Button */}
            <button
                onClick={handleViewFullAnalysis}
                className="btn btn-primary"
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-2)'
                }}
            >
                <Sparkles size={16} />
                View Full Analysis
                {hasMore && <span className="text-secondary">({creatives.length - 6} more)</span>}
                <ExternalLink size={14} />
            </button>
        </div>
    );
};

export default CreativesQuickView;
